import React from "react";
import { useSiteMetadata } from "../hooks/use-site-metadata";

export type ObjectType = "article" | "profile" | "website";

export interface ISEOProps extends React.PropsWithChildren {
  description?: string;
  image?: string;
  pathname?: string;
  title?: string;
  type?: ObjectType;
}

export const SEO = ({
  children,
  description,
  image,
  pathname,
  title,
  type = "website",
}: ISEOProps) => {
  const {
    banner: defaultBanner,
    description: defaultDescription,
    siteUrl,
    title: defaultTitle,
    twitterUsername,
  } = useSiteMetadata();

  const seo = {
    image: image || defaultBanner,
    description: description || defaultDescription,
    title: title ? `${title} | ${defaultTitle}` : defaultTitle,
    twitterUsername,
    url: `${siteUrl}${pathname || ""}`,
  };

  return (
    <>
      <html lang="ja" />
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta name="theme-color" content="#ef3340" />
      <title>{seo.title}</title>
      {/**
       * @see {@link https://ogp.me/#metadata|Basic metadata, The Open Graph protocol}
       */}
      <meta name="og:image" content={seo.image} />
      <meta name="og:image:url" content={seo.image} />
      <meta name="og:image:secure_url" content={seo.image} />
      <meta name="og:title" content={seo.title} />
      {/**
       * @see {@link https://ogp.me/#types|Object Types, The Open Graph protocol}
       * @see {@link https://ogp.me/#type_article|Object Types article - The Open Graph protocol}
       */}
      <meta name="og:type" content={type} />
      <meta name="og:url" content={seo.url} />

      {/**
       * Optional metadata
       * @see {@link https://ogp.me/#optional|Optional metadata, The Open Graph protocol}
       */}
      <meta name="og:locale" content="ja_JP" />
      <meta name="og:description" content={seo.description} />

      {/**
       * Twitter Card
       *
       * @see {@link https://developer.twitter.com/en/docs/twitter-for-websites/cards/guides/getting-started|Getting started, Twitter Developer Documentation}
       */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={seo.twitterUsername} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:site" content={seo.twitterUsername} />
      <meta name="twitter:url" content={seo.url} />
      {children}
    </>
  );
};
